import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, Mail, ArrowRight, AlertTriangle } from "lucide-react";
import { PaymentInformationSection } from "./PaymentInformationSection";

interface BookingSuccessProps {
  createdUserId?: string | null;
  verificationEmailSent?: boolean;
  bookingId?: string | null;
  paymentStatus?: string;
}

export const BookingSuccess: React.FC<BookingSuccessProps> = ({ 
  createdUserId,
  verificationEmailSent,
  bookingId,
  paymentStatus
}) => {
  const { t } = useTranslation();
  const isPaymentPending = paymentStatus === 'pending';
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const resendEmailVerification = async () => {
    try {
      toast.info(t('auth.verificationEmail.sending'));
      toast.success(t('auth.verificationEmail.sent', 'Verification email sent!'));
    } catch (error) {
      console.error("Failed to resend verification email:", error);
      toast.error(t('auth.verificationEmail.error', 'Failed to resend verification email'));
    }
  };
  
  return (
    <div>
      <div className="flex flex-col items-center text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            isPaymentPending ? "bg-amber-100" : "bg-green-100"
          }`}
        >
          {isPaymentPending ? (
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          ) : (
            <CheckCircle className="h-8 w-8 text-green-600" />
          )}
        </motion.div>
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-2xl font-display font-bold mb-3"
        >
          {isPaymentPending ? t('booking.paymentRequired.title', 'Booking Created - Payment Required') : t('booking.success.title')}
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-muted-foreground mb-6 max-w-md"
        >
          {isPaymentPending 
            ? t('booking.paymentRequired.description', 'Your booking has been created but requires payment to be completed. Please use the payment information below.')
            : t('booking.success.description')}
        </motion.p>

        {isPaymentPending && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="w-full mb-6"
          >
            <Alert variant="warning" className="bg-amber-50 border-amber-200 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">{t('booking.paymentRequired.alertTitle', 'Payment Required')}</AlertTitle>
              <AlertDescription className="text-amber-700">
                {t('booking.paymentRequired.alertDescription', 'Please complete the payment to confirm your booking. After payment, please send us the receipt via one of the contact methods below.')}
              </AlertDescription>
            </Alert>
            
            <PaymentInformationSection forSubscriptions={true} />
          </motion.div>
        )}

        {verificationEmailSent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 max-w-md"
          >
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-blue-800">{t('auth.verificationEmail.title', 'Email Verification Required')}</h3>
                <p className="text-sm text-blue-700 mt-1">
                  {t('auth.verificationEmail.description', "We've sent a verification email to your inbox. Please verify your email to access your account.")}
                </p>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-blue-600 px-0 mt-1"
                  onClick={resendEmailVerification}
                >
                  {t('auth.verificationEmail.resend', 'Resend verification email')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
        
        <div className="flex flex-col gap-3 sm:flex-row">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Button asChild>
              <Link to="/classes">
                {t('booking.success.browseClasses')}
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Button asChild variant="outline">
              <Link to={bookingId ? `/manage?id=${bookingId}` : "/dashboard"} className="flex items-center gap-2">
                {t('booking.success.viewBooking')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
